import React, { useState, useEffect, useRef } from "react";

// Range component that mimics the real Excalidraw Range behavior
const RangeDemo: React.FC<{
  value: number;
  onChange: (value: number) => void;
}> = ({ value, onChange }) => {
  const rangeRef = useRef<HTMLInputElement>(null);
  const valueRef = useRef<HTMLDivElement>(null);

  // Update positioning and background whenever value changes
  useEffect(() => {
    if (rangeRef.current && valueRef.current) {
      const rangeElement = rangeRef.current;
      const valueElement = valueRef.current;
      const inputWidth = rangeElement.offsetWidth;
      const thumbWidth = 15; // matches the real component (not 16)
      const position =
        (value / 100) * (inputWidth - thumbWidth) + thumbWidth / 2;
      valueElement.style.left = `${position}px`;
      rangeElement.style.background = `linear-gradient(to right, var(--color-slider-track) 0%, var(--color-slider-track) ${value}%, var(--button-bg) ${value}%, var(--button-bg) 100%)`;
    }
  }, [value]);

  // Initial setup on mount
  useEffect(() => {
    const timer = setTimeout(() => {
      if (rangeRef.current && valueRef.current) {
        const rangeElement = rangeRef.current;
        const valueElement = valueRef.current;
        const inputWidth = rangeElement.offsetWidth;
        const thumbWidth = 15; // matches the real component
        const position =
          (value / 100) * (inputWidth - thumbWidth) + thumbWidth / 2;
        valueElement.style.left = `${position}px`;
        rangeElement.style.background = `linear-gradient(to right, var(--color-slider-track) 0%, var(--color-slider-track) ${value}%, var(--button-bg) ${value}%, var(--button-bg) 100%)`;
      }
    }, 100); // Small delay to ensure DOM is ready

    return () => clearTimeout(timer);
  }, [value]);

  return (
    <div className="excalidraw">
      <label className="control-label">
        Opacity
        <div className="range-wrapper">
          <input
            ref={rangeRef}
            type="range"
            min="0"
            max="100"
            step="10"
            value={value}
            className="range-input"
            onChange={(e) => onChange(Number(e.target.value))}
          />
          <div className="value-bubble" ref={valueRef}>
            {value !== 0 ? value : null}
          </div>
          <div className="zero-label">0</div>
        </div>
      </label>
    </div>
  );
};

export const Components: React.FC = () => {
  const [copied, setCopied] = useState<string | null>(null);
  const [textValue, setTextValue] = useState<string>("");
  const [rangeValue, setRangeValue] = useState<number>(70);
  const [switchValue, setSwitchValue] = useState<boolean>(true);
  const [radioValue, setRadioValue] = useState<string>("1");
  const [dropdownOpen, setDropdownOpen] = useState<boolean>(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  const copyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    setCopied(text);
    setTimeout(() => setCopied(null), 2000);
  };

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setDropdownOpen(false);
      }
    };

    if (dropdownOpen) {
      document.addEventListener("mousedown", handleClickOutside);
    }

    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, [dropdownOpen]);

  return (
    <div className="ds-section">
      <div className="ds-section-header">
        <h2>🧩 Components</h2>
        <p>UI components used throughout Excalidraw with usage examples</p>
      </div>

      {/* Buttons */}
      <div className="component-category">
        <h3 className="ds-section-title">Buttons</h3>

        <div className="component-showcase">
          {/* Hamburger Menu Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button hamburger-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <line x1="4" y1="6" x2="20" y2="6" />
                  <line x1="4" y1="12" x2="20" y2="12" />
                  <line x1="4" y1="18" x2="20" y2="18" />
                </svg>
              </button>
            </div>
            <div className="component-info">
              <h4>Hamburger Menu</h4>
              <p>Main menu trigger button (top-left corner)</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<ToolButton icon={HamburgerMenuIcon} aria-label="Menu" />',
                  )
                }
              >
                <code>
                  &lt;ToolButton icon={`{HamburgerMenuIcon}`} aria-label="Menu"
                  /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Library Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button library-button">
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="1.5"
                >
                  <path d="M3 19a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                  <path d="M3 6a9 9 0 0 1 9 0a9 9 0 0 1 9 0" />
                  <line x1="3" y1="6" x2="3" y2="19" />
                  <line x1="12" y1="6" x2="12" y2="19" />
                  <line x1="21" y1="6" x2="21" y2="19" />
                </svg>
                <span>Library</span>
              </button>
            </div>
            <div className="component-info">
              <h4>Library Button</h4>
              <p>Sidebar trigger with icon + text (top-right corner)</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    "<DefaultSidebar.Trigger icon={LibraryIcon}>Library</DefaultSidebar.Trigger>",
                  )
                }
              >
                <code>
                  &lt;DefaultSidebar.Trigger icon={`{LibraryIcon}`}
                  &gt;Library&lt;/DefaultSidebar.Trigger&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Share Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button share-button">Share</button>
            </div>
            <div className="component-info">
              <h4>Share Button</h4>
              <p>Collaboration trigger button (top-right corner)</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<LiveCollaborationTrigger onSelect={() => console.log("share")} />',
                  )
                }
              >
                <code>
                  &lt;LiveCollaborationTrigger onSelect=
                  {`{() => console.log("share")}`} /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Primary Filled Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button primary-filled-button">
                Browse libraries
              </button>
            </div>
            <div className="component-info">
              <h4>Primary (Filled)</h4>
              <p>Primary action button with dark background</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="filled" color="primary" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="filled" color="primary" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Secondary Outlined Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button secondary-outlined-button">
                Cancel
              </button>
            </div>
            <div className="component-info">
              <h4>Secondary (Outlined)</h4>
              <p>Secondary action button with border only</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="outlined" color="primary" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="outlined" color="primary" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Danger Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button danger-button">Delete</button>
            </div>
            <div className="component-info">
              <h4>Danger</h4>
              <p>Destructive action button</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="filled" color="danger" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="filled" color="danger" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Muted Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button muted-button">Load more</button>
            </div>
            <div className="component-info">
              <h4>Muted</h4>
              <p>Low-emphasis button for less important actions</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="filled" color="muted" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="filled" color="muted" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Warning Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button warning-button">Warning</button>
            </div>
            <div className="component-info">
              <h4>Warning</h4>
              <p>Warning action button for cautionary actions</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="filled" color="warning" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="filled" color="warning" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Success Button */}
          <div className="component-item">
            <div className="component-preview">
              <button className="demo-button success-button">Success</button>
            </div>
            <div className="component-info">
              <h4>Success</h4>
              <p>
                Success state button (automatically applied after successful
                actions)
              </p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<FilledButton variant="filled" color="success" />',
                  )
                }
              >
                <code>
                  &lt;FilledButton variant="filled" color="success" /&gt;
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Properties Bar Buttons */}
        <h4 className="ds-section-title">Properties Bar Buttons</h4>
        <p>
          Button lists used in the App-menu__left properties panel context with
          proper CSS variables.
        </p>

        <div className="component-showcase">
          <div className="component-item">
            <div className="component-preview">
              <div className="excalidraw">
                <div
                  className="App-menu__left"
                  style={{
                    position: "relative",
                    width: "200px",
                    height: "auto",
                    padding: "0.75rem",
                  }}
                >
                  <div className="properties-bar">
                    <fieldset>
                      <legend>Text Alignment</legend>
                      <div className="buttonList">
                        <input
                          type="radio"
                          id="align-left"
                          name="text-align"
                          value="left"
                        />
                        <label htmlFor="align-left" title="Align left">
                          <div className="ToolIcon__icon">
                            <svg
                              aria-hidden="true"
                              focusable="false"
                              role="img"
                              viewBox="0 0 24 24"
                              fill="none"
                              strokeWidth="2"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <g
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth="2"
                              >
                                <path
                                  stroke="none"
                                  d="M0 0h24v24H0z"
                                  fill="none"
                                />
                                <line x1="4" y1="8" x2="20" y2="8" />
                                <line x1="4" y1="12" x2="12" y2="12" />
                                <line x1="4" y1="16" x2="16" y2="16" />
                              </g>
                            </svg>
                          </div>
                        </label>
                        <input
                          type="radio"
                          id="align-center"
                          name="text-align"
                          value="center"
                          defaultChecked
                        />
                        <label htmlFor="align-center" title="Align center">
                          <div className="ToolIcon__icon">
                            <svg
                              aria-hidden="true"
                              focusable="false"
                              role="img"
                              viewBox="0 0 24 24"
                              fill="none"
                              strokeWidth="2"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <g
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path
                                  stroke="none"
                                  d="M0 0h24v24H0z"
                                  fill="none"
                                />
                                <line x1="4" y1="8" x2="20" y2="8" />
                                <line x1="8" y1="12" x2="16" y2="12" />
                                <line x1="6" y1="16" x2="18" y2="16" />
                              </g>
                            </svg>
                          </div>
                        </label>
                        <input
                          type="radio"
                          id="align-right"
                          name="text-align"
                          value="right"
                        />
                        <label htmlFor="align-right" title="Align right">
                          <div className="ToolIcon__icon">
                            <svg
                              aria-hidden="true"
                              focusable="false"
                              role="img"
                              viewBox="0 0 24 24"
                              fill="none"
                              strokeWidth="2"
                              stroke="currentColor"
                              strokeLinecap="round"
                              strokeLinejoin="round"
                            >
                              <g
                                stroke="currentColor"
                                fill="none"
                                strokeLinecap="round"
                                strokeLinejoin="round"
                              >
                                <path
                                  stroke="none"
                                  d="M0 0h24v24H0z"
                                  fill="none"
                                />
                                <line x1="4" y1="8" x2="20" y2="8" />
                                <line x1="10" y1="12" x2="20" y2="12" />
                                <line x1="8" y1="16" x2="20" y2="16" />
                              </g>
                            </svg>
                          </div>
                        </label>
                      </div>
                    </fieldset>

                    <fieldset>
                      <legend>Fill Style</legend>
                      <div className="buttonList">
                        <input
                          type="radio"
                          id="fill-hachure"
                          name="fill-style"
                          value="hachure"
                          defaultChecked
                        />
                        <label htmlFor="fill-hachure" title="Hachure fill">
                          <div className="ToolIcon__icon">
                            <svg viewBox="0 0 20 20" width="20" height="20">
                              <path
                                d="M5.879 2.625h8.242a3.254 3.254 0 0 1 3.254 3.254v8.242a3.254 3.254 0 0 1-3.254 3.254H5.88a3.254 3.254 0 0 1-3.254-3.254V5.88a3.254 3.254 0 0 1 3.254-3.254Z"
                                stroke="currentColor"
                                strokeWidth="1.25"
                                fill="none"
                              />
                              <mask
                                id="FillHachureIconDS"
                                style={{ maskType: "alpha" }}
                                maskUnits="userSpaceOnUse"
                                x={2}
                                y={2}
                                width={16}
                                height={16}
                              >
                                <path
                                  d="M5.879 2.625h8.242a3.254 3.254 0 0 1 3.254 3.254v8.242a3.254 3.254 0 0 1-3.254 3.254H5.88a3.254 3.254 0 0 1-3.254-3.254V5.88a3.254 3.254 0 0 1 3.254-3.254Z"
                                  fill="currentColor"
                                  stroke="currentColor"
                                  strokeWidth="1.25"
                                />
                              </mask>
                              <g mask="url(#FillHachureIconDS)">
                                <path
                                  d="M2.258 15.156 15.156 2.258M7.324 20.222 20.222 7.325m-20.444 5.35L12.675-.222m-8.157 18.34L17.416 5.22"
                                  stroke="currentColor"
                                  strokeWidth="1.25"
                                  strokeLinecap="round"
                                  strokeLinejoin="round"
                                />
                              </g>
                            </svg>
                          </div>
                        </label>
                        <input
                          type="radio"
                          id="fill-cross-hatch"
                          name="fill-style"
                          value="cross-hatch"
                        />
                        <label
                          htmlFor="fill-cross-hatch"
                          title="Cross-hatch fill"
                        >
                          <div className="ToolIcon__icon">
                            <svg viewBox="0 0 20 20" width="20" height="20">
                              <g clipPath="url(#crossHatchClip)">
                                <path
                                  d="M5.879 2.625h8.242a3.254 3.254 0 0 1 3.254 3.254v8.242a3.254 3.254 0 0 1-3.254 3.254H5.88a3.254 3.254 0 0 1-3.254-3.254V5.88a3.254 3.254 0 0 1 3.254-3.254Z"
                                  stroke="currentColor"
                                  strokeWidth="1.25"
                                  fill="none"
                                />
                                <mask
                                  id="FillCrossHatchIconDS"
                                  style={{ maskType: "alpha" }}
                                  maskUnits="userSpaceOnUse"
                                  x={-1}
                                  y={-1}
                                  width={22}
                                  height={22}
                                >
                                  <path
                                    d="M2.426 15.044 15.044 2.426M7.383 20 20 7.383M0 12.617 12.617 0m-7.98 17.941L17.256 5.324m-2.211 12.25L2.426 4.956M20 12.617 7.383 0m5.234 20L0 7.383m17.941 7.98L5.324 2.745"
                                    stroke="currentColor"
                                    strokeWidth="1.25"
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                  />
                                </mask>
                                <g mask="url(#FillCrossHatchIconDS)">
                                  <path
                                    d="M14.121 2H5.88A3.879 3.879 0 0 0 2 5.879v8.242A3.879 3.879 0 0 0 5.879 18h8.242A3.879 3.879 0 0 0 18 14.121V5.88A3.879 3.879 0 0 0 14.121 2Z"
                                    fill="currentColor"
                                  />
                                </g>
                              </g>
                              <defs>
                                <clipPath id="crossHatchClip">
                                  <path fill="#fff" d="M0 0h20v20H0z" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </label>
                        <input
                          type="radio"
                          id="fill-solid"
                          name="fill-style"
                          value="solid"
                        />
                        <label htmlFor="fill-solid" title="Solid fill">
                          <div className="ToolIcon__icon">
                            <svg viewBox="0 0 20 20" width="20" height="20">
                              <g clipPath="url(#solidFillClip)">
                                <path
                                  d="M4.91 2.625h10.18a2.284 2.284 0 0 1 2.285 2.284v10.182a2.284 2.284 0 0 1-2.284 2.284H4.909a2.284 2.284 0 0 1-2.284-2.284V4.909a2.284 2.284 0 0 1 2.284-2.284Z"
                                  stroke="currentColor"
                                  strokeWidth="1.25"
                                  fill="currentColor"
                                />
                              </g>
                              <defs>
                                <clipPath id="solidFillClip">
                                  <path fill="#fff" d="M0 0h20v20H0z" />
                                </clipPath>
                              </defs>
                            </svg>
                          </div>
                        </label>
                      </div>
                    </fieldset>
                  </div>
                </div>
              </div>
            </div>
            <div className="component-info">
              <h4>App-menu__left ButtonList</h4>
              <p>
                Properties panel buttons with proper CSS variable context and
                styling
              </p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<div className="App-menu__left"><div className="buttonList">...</div></div>',
                  )
                }
              >
                <code>
                  &lt;div className="App-menu__left"&gt;&lt;div
                  className="buttonList"&gt;...&lt;/div&gt;&lt;/div&gt;
                </code>
              </div>
            </div>
          </div>
        </div>

        {/* Button Organization Guidelines */}
        <div className="component-guidelines">
          <h4 className="ds-section-title">Button Organization by Context</h4>

          <div className="guideline-section">
            <h5>1. Main Menu (Hamburger Menu)</h5>
            <p>
              Uses <code>DropdownMenuItem</code> components with consistent
              structure:
            </p>
            <ul>
              <li>
                <strong>Structure:</strong> Icon + Text + Optional Shortcut
              </li>
              <li>
                <strong>Height:</strong> 2rem (32px) standard
              </li>
              <li>
                <strong>Examples:</strong> Load Scene, Save, Export, Help, Clear
                Canvas
              </li>
              <li>
                <strong>Styling:</strong> Transparent background, hover effects,
                keyboard shortcuts
              </li>
            </ul>
            <div className="code-example">
              <code>
                &lt;DropdownMenuItem icon={`{LoadIcon}`}{" "}
                shortcut="Ctrl+O"&gt;Load&lt;/DropdownMenuItem&gt;
              </code>
            </div>
          </div>

          <div className="guideline-section">
            <h5>2. Properties Bar (Right Panel)</h5>
            <p>
              Uses <code>ToolButton</code> and form controls for shape
              properties:
            </p>
            <ul>
              <li>
                <strong>Icon Buttons:</strong> Small square buttons for style
                toggles
              </li>
              <li>
                <strong>Color Pickers:</strong> Specialized color selection
                buttons
              </li>
              <li>
                <strong>Input Fields:</strong> Numeric inputs with label styling
              </li>
              <li>
                <strong>Button Lists:</strong> Radio-style button groups
              </li>
            </ul>
            <div className="code-example">
              <code>
                &lt;ToolButton type="icon" icon={`{StrokeIcon}`} selected=
                {`{isSelected}`} /&gt;
              </code>
            </div>
          </div>

          <div className="guideline-section">
            <h5>3. Sidebar (Left Panel)</h5>
            <p>
              Uses <code>SidebarTrigger</code> and tab-based navigation:
            </p>
            <ul>
              <li>
                <strong>Trigger Button:</strong> Checkbox-style toggle with icon
              </li>
              <li>
                <strong>Tab Triggers:</strong> Square buttons in a grouped
                container
              </li>
              <li>
                <strong>Primary Actions:</strong> Filled buttons like "Browse
                libraries"
              </li>
              <li>
                <strong>Dock Controls:</strong> Icon buttons for
                docking/undocking
              </li>
            </ul>
            <div className="code-example">
              <code>
                &lt;SidebarTrigger name="library" icon={`{LibraryIcon}`}{" "}
                title="Library" /&gt;
              </code>
            </div>
          </div>

          <div className="guideline-section">
            <h5>4. Modal Dialogs</h5>
            <p>
              Uses <code>DialogActionButton</code> and <code>FilledButton</code>
              :
            </p>
            <ul>
              <li>
                <strong>Primary Actions:</strong> Dark background (confirm,
                submit)
              </li>
              <li>
                <strong>Danger Actions:</strong> Red background (delete, clear)
              </li>
              <li>
                <strong>Secondary Actions:</strong> Transparent with border
                (cancel)
              </li>
              <li>
                <strong>Height:</strong> 3rem (48px) for prominence
              </li>
            </ul>
            <div className="code-example">
              <code>
                &lt;DialogActionButton actionType="primary" label="Confirm"
                /&gt;
              </code>
            </div>
          </div>
        </div>
      </div>

      {/* Form Fields */}
      <div className="component-category">
        <h3 className="ds-section-title">Form Fields</h3>

        <div className="component-showcase">
          {/* Text Field */}
          <div className="component-item">
            <div className="component-preview">
              <div className="excalidraw">
                <div className="ExcTextField">
                  <div className="ExcTextField__label">Label</div>
                  <div className="ExcTextField__input">
                    <input
                      placeholder="Enter text..."
                      value={textValue}
                      onChange={(e) => setTextValue(e.target.value)}
                    />
                  </div>
                </div>
              </div>
            </div>
            <div className="component-info">
              <h4>TextField</h4>
              <p>Text input with label and placeholder support</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<TextField label="Label" placeholder="Enter text..." value={value} onChange={setValue} />',
                  )
                }
              >
                <code>
                  &lt;TextField label="Label" placeholder="Enter text..." value=
                  {`{value}`} onChange={`{setValue}`} /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Range Slider */}
          <div className="component-item">
            <div className="component-preview">
              <RangeDemo value={rangeValue} onChange={setRangeValue} />
            </div>
            <div className="component-info">
              <h4>Range</h4>
              <p>Slider component for numeric value selection</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<Range updateData={updateData} app={app} testId="opacity" />',
                  )
                }
              >
                <code>
                  &lt;Range updateData={`{updateData}`} app={`{app}`}{" "}
                  testId="opacity" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Switch */}
          <div className="component-item">
            <div className="component-preview">
              <div className="excalidraw">
                <div className={`Switch ${switchValue ? "toggled" : ""}`}>
                  <input
                    type="checkbox"
                    checked={switchValue}
                    onChange={(e) => setSwitchValue(e.target.checked)}
                  />
                </div>
              </div>
            </div>
            <div className="component-info">
              <h4>Switch</h4>
              <p>Toggle switch for boolean settings</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<Switch checked={checked} onChange={setChecked} title="Toggle option" />',
                  )
                }
              >
                <code>
                  &lt;Switch checked={`{checked}`} onChange={`{setChecked}`}{" "}
                  title="Toggle option" /&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Radio Group */}
          <div className="component-item">
            <div className="component-preview">
              <div className="excalidraw">
                <div className="RadioGroup">
                  <div
                    className={`RadioGroup__choice ${
                      radioValue === "1" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="demo"
                      value="1"
                      checked={radioValue === "1"}
                      onChange={(e) => setRadioValue(e.target.value)}
                    />
                    1
                  </div>
                  <div
                    className={`RadioGroup__choice ${
                      radioValue === "2" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="demo"
                      value="2"
                      checked={radioValue === "2"}
                      onChange={(e) => setRadioValue(e.target.value)}
                    />
                    2
                  </div>
                  <div
                    className={`RadioGroup__choice ${
                      radioValue === "3" ? "active" : ""
                    }`}
                  >
                    <input
                      type="radio"
                      name="demo"
                      value="3"
                      checked={radioValue === "3"}
                      onChange={(e) => setRadioValue(e.target.value)}
                    />
                    3
                  </div>
                </div>
              </div>
            </div>
            <div className="component-info">
              <h4>RadioGroup</h4>
              <p>Radio button group for single selection</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    '<RadioGroup choices={choices} value={value} onChange={setValue} name="demo" />',
                  )
                }
              >
                <code>
                  &lt;RadioGroup choices={`{choices}`} value={`{value}`}{" "}
                  onChange={`{setValue}`} name="demo" /&gt;
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Form Elements */}
      <div className="component-category">
        <h3 className="ds-section-title">Form Elements</h3>

        <div className="component-showcase">
          {/* Dropdown Menu */}
          <div className="component-item">
            <div className="component-preview">
              <div className="dropdown-demo" ref={dropdownRef}>
                <button
                  className="demo-button dropdown-trigger dropdown-trigger--menu"
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                >
                  Options
                </button>
                {dropdownOpen && (
                  <div className="dropdown-menu-preview">
                    <div
                      className="dropdown-menu-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Edit
                    </div>
                    <div
                      className="dropdown-menu-item"
                      onClick={() => setDropdownOpen(false)}
                    >
                      Delete
                    </div>
                  </div>
                )}
              </div>
            </div>
            <div className="component-info">
              <h4>Dropdown Menu</h4>
              <p>Contextual menu with items and separators</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    `<DropdownMenu open={isOpen}>
  <DropdownMenu.Trigger>
    <Button onSelect={() => setIsOpen(!isOpen)}>
      Options
    </Button>
  </DropdownMenu.Trigger>
  <DropdownMenu.Content>
    <DropdownMenu.Item onSelect={() => console.log('Edit')}>
      Edit
    </DropdownMenu.Item>
    <DropdownMenu.Item onSelect={() => console.log('Delete')}>
      Delete
    </DropdownMenu.Item>
  </DropdownMenu.Content>
</DropdownMenu>`,
                  )
                }
              >
                <code>
                  &lt;DropdownMenu open={`{isOpen}`}&gt;
                  <br />
                  &nbsp;&nbsp;&lt;DropdownMenu.Trigger&gt;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&lt;Button&gt;Options&lt;/Button&gt;
                  <br />
                  &nbsp;&nbsp;&lt;/DropdownMenu.Trigger&gt;
                  <br />
                  &nbsp;&nbsp;&lt;DropdownMenu.Content&gt;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&lt;DropdownMenu.Item&gt;Edit&lt;/DropdownMenu.Item&gt;
                  <br />
                  &nbsp;&nbsp;&nbsp;&nbsp;&lt;DropdownMenu.Item&gt;Delete&lt;/DropdownMenu.Item&gt;
                  <br />
                  &nbsp;&nbsp;&lt;/DropdownMenu.Content&gt;
                  <br />
                  &lt;/DropdownMenu&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Select Dropdown */}
          <div className="component-item">
            <div className="component-preview">
              <select className="dropdown-select demo-select">
                <option value="option1">Option 1</option>
                <option value="option2" selected>
                  Option 2
                </option>
                <option value="option3">Option 3</option>
              </select>
            </div>
            <div className="component-info">
              <h4>Select Dropdown</h4>
              <p>Standard HTML select with Excalidraw styling</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    `<select 
  className="dropdown-select"
  value={value}
  onChange={(e) => setValue(e.target.value)}
>
  <option value="option1">Option 1</option>
  <option value="option2">Option 2</option>
</select>`,
                  )
                }
              >
                <code>
                  &lt;select className="dropdown-select"&gt;
                  <br />
                  &nbsp;&nbsp;&lt;option value="option1"&gt;Option
                  1&lt;/option&gt;
                  <br />
                  &nbsp;&nbsp;&lt;option value="option2"&gt;Option
                  2&lt;/option&gt;
                  <br />
                  &lt;/select&gt;
                </code>
              </div>
            </div>
          </div>

          {/* Language Selector */}
          <div className="component-item">
            <div className="component-preview">
              <select className="dropdown-select dropdown-select__language">
                <option value="en">English</option>
                <option value="es">Español</option>
                <option value="fr">Français</option>
                <option value="de">Deutsch</option>
              </select>
            </div>
            <div className="component-info">
              <h4>Language Selector</h4>
              <p>Specialized dropdown for language selection</p>
              <div
                className="component-code"
                onClick={() =>
                  copyToClipboard(
                    `import { LanguageList } from "./app-language/LanguageList";

<LanguageList style={{ width: "120px" }} />`,
                  )
                }
              >
                <code>
                  import {`{ LanguageList }`} from
                  "./app-language/LanguageList";
                  <br />
                  <br />
                  &lt;LanguageList style={`{{ width: "120px" }}`} /&gt;
                </code>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Usage Guidelines */}
      <div className="component-category">
        <h3 className="ds-section-title">Usage Guidelines</h3>
        <div className="guidelines-grid">
          <div className="guideline-item">
            <h4>Component Structure</h4>
            <p>
              All components follow TypeScript interfaces and accept standard
              HTML props alongside custom Excalidraw props.
            </p>
            <div
              className="code-block"
              onClick={() =>
                copyToClipboard(
                  "interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement>",
                )
              }
            >
              <code>
                interface ButtonProps extends
                React.ButtonHTMLAttributes&lt;HTMLButtonElement&gt;
              </code>
            </div>
          </div>

          <div className="guideline-item">
            <h4>Event Handling</h4>
            <p>
              Components use <code>onSelect</code> for primary actions and
              compose with existing event handlers.
            </p>
            <ul>
              <li>
                <strong>Button:</strong> <code>onSelect</code> callback
              </li>
              <li>
                <strong>TextField:</strong> <code>onChange</code> with string
                value
              </li>
              <li>
                <strong>Switch:</strong> <code>onChange</code> with boolean
                value
              </li>
            </ul>
          </div>

          <div className="guideline-item">
            <h4>Styling</h4>
            <p>
              Components use CSS custom properties for theming and can be
              customized via <code>className</code> prop.
            </p>
            <div
              className="code-block"
              onClick={() =>
                copyToClipboard(
                  '<Button className="custom-style">Custom Button</Button>',
                )
              }
            >
              <code>
                &lt;Button className="custom-style"&gt;Custom
                Button&lt;/Button&gt;
              </code>
            </div>
          </div>

          <div className="guideline-item">
            <h4>Accessibility</h4>
            <ul>
              <li>
                <strong>Keyboard navigation</strong> - All interactive
                components support keyboard input
              </li>
              <li>
                <strong>Screen readers</strong> - Proper ARIA labels and
                semantic HTML
              </li>
              <li>
                <strong>Focus management</strong> - Visible focus indicators and
                logical tab order
              </li>
            </ul>
          </div>

          <div className="guideline-item">
            <h4>State Management</h4>
            <p>
              Components are controlled - state should be managed by parent
              components.
            </p>
            <div
              className="code-block"
              onClick={() =>
                copyToClipboard(
                  'const [value, setValue] = useState("");\n<TextField value={value} onChange={setValue} />',
                )
              }
            >
              <code>
                const [value, setValue] = useState("");
                <br />
                &lt;TextField value={`{value}`} onChange={`{setValue}`} /&gt;
              </code>
            </div>
          </div>

          <div className="guideline-item">
            <h4>Import Paths</h4>
            <p>
              Import components from their specific paths within the Excalidraw
              package.
            </p>
            <div
              className="code-block"
              onClick={() =>
                copyToClipboard(
                  'import { Button } from "@excalidraw/excalidraw/components/Button";',
                )
              }
            >
              <code>
                import {`{ Button }`} from
                "@excalidraw/excalidraw/components/Button";
              </code>
            </div>
          </div>
        </div>
      </div>

      {/* Copy Notification */}
      {copied && (
        <div className="copy-notification">Copied to clipboard: {copied}</div>
      )}
    </div>
  );
};
